// src/pages/AdminTeachersIncomeReport.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";
import { useLessons } from "../contexts/LessonsContext";
import AdminTeachersIncomeHeader from "../components/ui/AdminTeachersIncomeHeader";
import MonthPicker from "../components/ui/Monthpicker";

/* ===================== Helpers ===================== */
const cls = (...a) => a.filter(Boolean).join(" ");

const toISODate = (d) => {
  if (!d) return "";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const todayISO = () => toISODate(new Date());

const toISOMonth = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
};

const currentMonthISO = () => toISOMonth(new Date());

const startOfSelectedMonthISO = (monthISO) => {
  if (!monthISO) return "";
  const [y, m] = String(monthISO).split("-").map(Number);
  if (!y || !m) return "";
  return toISODate(new Date(y, m - 1, 1));
};

const endOfSelectedMonthISO = (monthISO) => {
  if (!monthISO) return "";
  const [y, m] = String(monthISO).split("-").map(Number);
  if (!y || !m) return "";
  return toISODate(new Date(y, m, 0));
};

const isSameMonth = (monthISO, d = new Date()) => monthISO === toISOMonth(d);

const rangeFromMonth = (monthISO) => {
  const start = startOfSelectedMonthISO(monthISO);
  let end = endOfSelectedMonthISO(monthISO);
  if (isSameMonth(monthISO)) end = todayISO();
  return { start, end };
};

const money = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pct = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return `${v.toFixed(0)}%`;
};

// Backend now returns both teachers and suppliers in this report (role: 'teacher' | 'supplier').
const roleLabel = (role) => (role === "supplier" ? "ספק" : "מורה");

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/* ===================== Small pieces ===================== */
const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
      role === "supplier"
        ? "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
        : "bg-indigo-50 text-indigo-700 border-indigo-200"
    }`}
  >
    {roleLabel(role)}
  </span>
);

/* ===================== CSV Export ===================== */
const downloadCSV = (rows, filename = "teachers_income.csv") => {
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2500);
};

/* ===================== Page ===================== */
export default function AdminTeachersIncomeReport() {
  const { getAdminTeachersIncomeReport, getAdminTeacherIncomeDetails } = useLessons();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [month, setMonth] = useState(currentMonthISO());

  const initialRange = useMemo(() => rangeFromMonth(currentMonthISO()), []);
  const [start, setStart] = useState(initialRange.start);
  const [end, setEnd] = useState(initialRange.end);

  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");

  const [sort, setSort] = useState("income_desc");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [totals, setTotals] = useState({
    grossIncome: 0,
    netIncome: 0,
    tax: 0,
    commission: 0,
    count: 0,
    teachers: 0,
    teachersCount: 0,
    suppliersCount: 0,
  });

  const [selectedTeacher, setSelectedTeacher] = useState(null);

  useEffect(() => {
    const r = rangeFromMonth(month);
    setStart(r.start);
    setEnd(r.end);
    setPage(1);
    setSelectedTeacher(null);
  }, [month]);

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const l = Number(limit || 20);
    return Math.max(1, Math.ceil(t / l));
  }, [total, limit]);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await getAdminTeachersIncomeReport({ start, end, search, sort, page, limit });
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
      setMeta(data?.meta || data?.raw?.meta || {});
      setTotal(Number(data?.total ?? list.length ?? 0));
      const t = data?.totals || {};
      setTotals({
        grossIncome: Number(t?.grossIncome ?? t?.income ?? 0),
        netIncome: Number(t?.netIncome ?? 0),
        tax: Number(t?.tax ?? 0),
        commission: Number(t?.commission ?? 0),
        count: Number(t?.count ?? 0),
        teachers: Number(t?.teachers ?? list.length ?? 0),
        teachersCount: Number(t?.teachersCount ?? 0),
        suppliersCount: Number(t?.suppliersCount ?? 0),
      });
    } catch (e) {
      setError(e?.message || "שגיאה בטעינת הדוח");
      setItems([]);
      setTotal(0);
      setMeta({});
      setTotals({ grossIncome: 0, netIncome: 0, tax: 0, commission: 0, count: 0, teachers: 0, teachersCount: 0, suppliersCount: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [getAdminTeachersIncomeReport, start, end, search, sort, page, limit]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const applyFilters = () => {
    setPage(1);
    setSearch(searchDraft.trim());
  };

  const exportAllVisible = () => {
    const rows = [
      ["ID", "Role", "Name", "Email", "Purchases", "Gross Income", "Commission", "Tax %", "Tax", "Net Income"],
      ...items.map((t) => {
        const fullName = `${t.firstName || ""} ${t.lastName || ""}`.trim();
        const teacherName = t.teacherName ?? (fullName || "—");
        const purchasesCount = Number(t.purchasesCount ?? t.count ?? 0);
        const gross = Number(t.grossIncome ?? t.income ?? 0);
        const commission = Number(t.commissionAmount ?? 0);
        const taxPct = t.taxPercent ?? null;
        const tax = Number(t.taxAmount ?? 0);
        const net = Number(t.netIncome ?? 0);
        return [t.teacherId ?? t.id ?? "", t.role || "teacher", teacherName, t.email || "", purchasesCount, money(gross), money(commission), pct(taxPct), money(tax), money(net)];
      }),
    ];
    downloadCSV(rows, `teachers_income_${month || "NA"}.csv`);
  };

  const handleSelectTeacher = async (teacher) => {
    // The per-course breakdown endpoint is teacher-only — a supplier's
    // revenue isn't attributed to a single teacher_id, so there's no
    // equivalent drill-down to fetch for a supplier row.
    if (teacher.role === "supplier") {
      setSelectedTeacher({ ...teacher, breakdown: [], breakdownLoading: false, breakdownError: "", supplierNoDetails: true });
      setTimeout(() => {
        document.getElementById("teacher-details-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }

    setSelectedTeacher({ ...teacher, breakdown: [], breakdownLoading: true, breakdownError: "" });

    // On mobile/tablet, scroll to the details panel
    setTimeout(() => {
      document.getElementById("teacher-details-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);

    try {
      const details = await getAdminTeacherIncomeDetails({
        teacherId: teacher.id || teacher.teacherId,
        start,
        end,
        sort: "income_desc",
        limit: 200,
      });
      setSelectedTeacher((prev) => {
        if (!prev) return prev;
        return { ...prev, breakdown: Array.isArray(details.items) ? details.items : [], breakdownLoading: false };
      });
    } catch (e) {
      setSelectedTeacher((prev) => {
        if (!prev) return prev;
        return { ...prev, breakdownLoading: false, breakdownError: e?.message || "שגיאה בטעינת פירוט קורסים" };
      });
    }
  };

  return (
    <>
      <AdminTeachersIncomeHeader />

      {isLoading && <SpinnerOverlay variant="glow" text="אנא המתן..." dark={false} blur={true} />}

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">

            <MonthPicker
              value={month}
              onChange={(v) => setMonth(String(v || "").trim())}
            />

            {/* Search */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); applyFilters(); }
                  }}
                  placeholder="חיפוש לפי שם, מייל או טלפון..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setSearch(searchDraft.trim()); setPage(1); loadReport(); }}
                disabled={isLoading}
                className="flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isLoading ? "טוען..." : "טען"}
              </button>

              <button
                type="button"
                onClick={exportAllVisible}
                className="flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                title="ייצא לקובץ CSV"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <DownloadIcon />
                  CSV
                </span>
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">👥</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-blue-700">מורים / ספקים בדוח</p>
                <p className="text-2xl font-bold text-blue-900">{Number(totals.teachers || 0).toLocaleString("he-IL")}</p>
                <p className="text-[11px] text-blue-600 mt-0.5">
                  {Number(totals.teachersCount || 0).toLocaleString("he-IL")} מורים · {Number(totals.suppliersCount || 0).toLocaleString("he-IL")} ספקים
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">🧾</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-purple-700">סה״כ הכנסות</p>
                <p className="text-2xl font-bold text-purple-900">₪{money(totals.grossIncome)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">₪</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-green-700">סה״כ נטו</p>
                <p className="text-2xl font-bold text-green-900">₪{money(totals.netIncome)}</p>
              </div>
            </div>
          </div>

        </div>

        {/* ── Main two-panel grid ── */}
        {/* Splits at lg (1024px): table left 6/10, details right 4/10 */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">

          {/* ── Teachers Table — 6 cols on lg+ ── */}
          <div className="lg:col-span-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Table header */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <h2 className="text-base font-bold text-gray-900">פירוט הכנסות מורים וספקים</h2>
                  <div className="text-sm text-gray-600 font-medium">
                    עמוד {page} מתוך {totalPages} • {Number(total || 0).toLocaleString("he-IL")} שורות
                  </div>
                </div>
              </div>

              {/* Scrollable table */}
              {/* min-w reduced: on sm only 5 cols visible (commission/tax hidden below lg) */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] lg:min-w-[600px]">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">שם</th>
                      <th className="px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">רכישות</th>
                      <th className="px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">ברוטו</th>
                      {/* Hidden below lg — these two columns appear only when there's room */}
                      <th className="hidden lg:table-cell px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">עמלה</th>
                      <th className="hidden lg:table-cell px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">מס</th>
                      <th className="px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700">נטו</th>
                      <th className="px-4 sm:px-6 py-3 text-right text-sm font-bold text-gray-700"></th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {(items || []).map((t, idx) => {
                      const teacherId = t.teacherId ?? t.id ?? idx;
                      const fullName = `${t.firstName || ""} ${t.lastName || ""}`.trim();
                      const teacherName = t.teacherName ?? (fullName || "—");

                      const purchasesCount = Number(t.purchasesCount ?? t.count ?? 0);
                      const gross = Number(t.grossIncome ?? t.income ?? 0);
                      const commission = Number(t.commissionAmount ?? 0);
                      const tax = Number(t.taxAmount ?? 0);
                      const net = Number(t.netIncome ?? 0);

                      const active = (selectedTeacher?.id || selectedTeacher?.teacherId) === (t.id || t.teacherId)
                        && (selectedTeacher?.role || "teacher") === (t.role || "teacher");

                      return (
                        <tr
                          key={`${t.role || "teacher"}-${teacherId}`}
                          className={cls(
                            "transition-colors",
                            active ? "bg-indigo-50/60 hover:bg-indigo-50/80" : "hover:bg-gray-50"
                          )}
                        >
                          {/* Teacher/supplier info */}
                          <td className="px-4 sm:px-6 py-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0 text-sm sm:text-base">
                                {teacherName.charAt(0)}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <div className="text-sm font-semibold text-gray-900 truncate max-w-[120px] sm:max-w-none">
                                    {teacherName}
                                  </div>
                                  <RoleBadge role={t.role} />
                                </div>
                                {t.email && (
                                  <div className="text-xs text-gray-500 truncate hidden sm:block">
                                    {t.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Purchases */}
                          <td className="px-4 sm:px-6 py-4">
                            <span className="inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs sm:text-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                              {purchasesCount.toLocaleString("he-IL")}
                            </span>
                          </td>

                          {/* Gross */}
                          <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                            ₪{money(gross)}
                          </td>

                          {/* Commission — hidden below lg */}
                          <td className="hidden lg:table-cell px-4 sm:px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            ₪{money(commission)}
                          </td>

                          {/* Tax — hidden below lg */}
                          <td className="hidden lg:table-cell px-4 sm:px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                            ₪{money(tax)}
                          </td>

                          {/* Net */}
                          <td className="px-4 sm:px-6 py-4">
                            <span className="inline-flex items-center px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                              ₪{money(net)}
                            </span>
                          </td>

                          {/* Action */}
                          <td className="px-4 sm:px-6 py-4">
                            <button
                              onClick={() =>
                                handleSelectTeacher({
                                  ...t,
                                  id: t.id ?? t.teacherId,
                                  role: t.role || "teacher",
                                  teacherName,
                                  purchasesCount,
                                  grossIncome: gross,
                                  netIncome: net,
                                  taxAmount: tax,
                                  commissionAmount: commission,
                                  taxPercent: t.taxPercent ?? null,
                                  avgGross: purchasesCount ? gross / purchasesCount : 0,
                                })
                              }
                              className={cls(
                                "px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap",
                                active
                                  ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                              )}
                            >
                              {active ? "✓ נבחר" : "פירוט"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {!items?.length && (
                      <tr>
                        <td className="px-6 py-16 text-center text-gray-500" colSpan={7}>
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-4xl opacity-50">📭</span>
                            <span className="font-medium">לא נמצאו תוצאות</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page >= totalPages
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                   → הבא
                  </button>

                  <div className="text-sm text-gray-700 font-semibold">
                    <span className="text-gray-900">{page}</span>
                    <span className="text-gray-500 mx-1">/</span>
                    <span className="text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page <= 1
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                     הקודם ←
                  </button>

                </div>
              </div>
            </div>
          </div>

          {/* ── Details Panel — 4 cols on lg+ ── */}
          {/* id used for mobile scroll-into-view */}
          <div id="teacher-details-panel" className="lg:col-span-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-6">
              {!selectedTeacher ? (
                <div className="p-8">
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <span className="text-3xl sm:text-4xl">👤</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">בחר מורה או ספק להצגת פירוט</h3>
                    <p className="text-gray-600 text-sm">לחץ על "פירוט" ליד השורה לצפייה במידע מפורט</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Details header */}
                  <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
                          {selectedTeacher.teacherName?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                              {selectedTeacher.teacherName}
                            </h3>
                            <RoleBadge role={selectedTeacher.role} />
                          </div>
                          {selectedTeacher.email && (
                            <p className="text-xs sm:text-sm text-gray-600 truncate">
                              {selectedTeacher.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedTeacher(null)}
                        className="p-2 rounded-xl hover:bg-white/80 border border-indigo-200 transition-colors flex-shrink-0"
                        title="סגור"
                      >
                        <XIcon />
                      </button>
                    </div>
                  </div>

                  {/* Financial summary */}
                  <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">הכנסה ברוטו</span>
                        <span className="font-bold text-gray-900">₪{money(selectedTeacher.grossIncome ?? 0)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">עמלת פלטפורמה</span>
                        <span className="font-semibold text-gray-900">₪{money(selectedTeacher.commissionAmount ?? 0)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">ניכוי מס ({pct(selectedTeacher.taxPercent ?? null)})</span>
                        <span className="font-semibold text-gray-900">₪{money(selectedTeacher.taxAmount ?? 0)}</span>
                      </div>
                      <div className="pt-3 border-t-2 border-gray-300 flex items-center justify-between">
                        <span className="text-gray-700 font-bold">סה״כ נטו</span>
                        <span className="text-xl font-bold text-emerald-600">₪{money(selectedTeacher.netIncome ?? 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Courses breakdown — teachers only */}
                  <div className="p-4 sm:p-6">
                    <h4 className="text-sm font-bold text-gray-900 mb-4">פירוט לפי קורסים</h4>

                    {selectedTeacher.supplierNoDetails ? (
                      <div className="text-center py-8 text-gray-500">
                        <span className="text-3xl block mb-2">🏢</span>
                        <span className="text-sm">
                          פירוט לפי קורסים זמין כרגע רק עבור מורים.
                          <br />
                          הכנסת הספק מסוכמת מכלל הקורסים שהספק יצר.
                        </span>
                      </div>
                    ) : selectedTeacher.breakdownLoading ? (
                      <div className="flex items-center justify-center py-8 text-gray-600">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-400 border-t-transparent ml-3" />
                        <span className="text-sm font-medium">טוען נתונים...</span>
                      </div>
                    ) : selectedTeacher.breakdownError ? (
                      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
                        <div className="font-semibold mb-1">שגיאה בטעינת נתונים</div>
                        <div>{selectedTeacher.breakdownError}</div>
                      </div>
                    ) : !selectedTeacher.breakdown?.length ? (
                      <div className="text-center py-8 text-gray-500">
                        <span className="text-3xl block mb-2">📚</span>
                        <span className="text-sm">אין קורסים להצגה</span>
                      </div>
                    ) : (
                      // On mobile: max-h is shorter so the panel doesn't dominate;
                      // on lg+ (sticky panel): taller to use available viewport height
                      <div className="space-y-3 max-h-[320px] sm:max-h-[400px] lg:max-h-[480px] overflow-y-auto pl-1">
                        {selectedTeacher.breakdown.map((c, idx) => {
                          const courseId = c.courseId ?? c.id ?? idx;
                          const title = c.title || c.courseTitle || `קורס #${courseId}`;

                          const doneLessonsCount = Number(c.doneLessonsCount ?? c.count ?? 0);
                          const cancelledLessonsCount = Number(c.cancelledLessonsCount ?? 0);
                          const notHeldLessonsCount = Number(c.notHeldLessonsCount ?? 0);
                          const upcomingLessonsCount = Number(c.upcomingLessonsCount ?? 0);

                          const paidDoneGross = Number(c.paidDoneGross ?? c.income ?? 0);
                          const paidCancelledGross = Number(c.paidCancelledGross ?? 0);
                          const paidNotHeldGross = Number(c.paidNotHeldGross ?? 0);
                          const paidUpcomingGross = Number(c.paidUpcomingGross ?? 0);
                          const paidTotalGross = Number(c.paidTotalGross ?? 0);

                          return (
                            <div
                              key={courseId}
                              className="p-3 sm:p-4 rounded-xl border border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/30 transition-all"
                            >
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <h5 className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1">
                                  {title}
                                </h5>
                                <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
                                  <span className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                                    בוצעו: {doneLessonsCount.toLocaleString("he-IL")}
                                  </span>
                                  {!!cancelledLessonsCount && paidCancelledGross > 0 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-bold bg-red-50 text-red-700 border border-red-200 whitespace-nowrap">
                                      בוטלו: {cancelledLessonsCount.toLocaleString("he-IL")}
                                    </span>
                                  )}
                                  {!!notHeldLessonsCount && paidNotHeldGross > 0 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                                      לא התקיימו: {notHeldLessonsCount.toLocaleString("he-IL")}
                                    </span>
                                  )}
                                  {!!upcomingLessonsCount && paidUpcomingGross > 0 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                                      עתידיים: {upcomingLessonsCount.toLocaleString("he-IL")}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 gap-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">שיעורים שבוצעו בפועל</span>
                                  <span className="font-bold text-emerald-600 whitespace-nowrap">₪{money(paidDoneGross)}</span>
                                </div>
                                {paidCancelledGross > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">שיעורים שבוטלו</span>
                                    <span className="font-semibold text-red-600 whitespace-nowrap">₪{money(paidCancelledGross)}</span>
                                  </div>
                                )}
                                {paidNotHeldGross > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">שיעורים שלא התקיימו</span>
                                    <span className="font-semibold text-amber-600 whitespace-nowrap">₪{money(paidNotHeldGross)}</span>
                                  </div>
                                )}
                                {paidUpcomingGross > 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">שיעורים שטרם התקיימו</span>
                                    <span className="font-semibold text-blue-600 whitespace-nowrap">₪{money(paidUpcomingGross)}</span>
                                  </div>
                                )}
                                <div className="pt-2 mt-1 border-t border-gray-200 flex items-center justify-between">
                                  <span className="text-gray-800 font-bold">סה״כ תשלומים</span>
                                  <span className="font-bold text-gray-900 whitespace-nowrap">₪{money(paidTotalGross)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}